import React from 'react';
import { Theme, ThemeProps } from '@theme/base';
import { withTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { isMobile } from '@styles/breakpoints';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import { TripTypes, TripTypeKeys } from '@model/common';
import { NavDestination } from '@model/content';
import { displayTimes } from '@model/common/opening-times';
import { isTourTripType } from '@util/tours';

/* ***************** *
 *       Types       *
 * ***************** */

export interface CallToBookProps {
  theme: Theme;
  tripType: TripTypes;
  destination?: NavDestination;
  testId?: string;
}

/* ***************** *
 *       Styles      *
 * ***************** */
interface ContainerProps extends ThemeProps {
  isMobile: boolean;
}
const Container: any = styled.div(({ theme, isMobile }: ContainerProps) => ({
  background: theme.custom.colors.group2.dark,
  borderRadius: 16,
  padding: isMobile ? '25px' : theme.custom.spacing.xxLarge
}));

const Header: any = styled.h3(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h3 as any),
  color: theme.custom.colors.group1.base,
  margin: 0
}));

const SubHeader: any = styled.h4(({ theme, isMobile }: ContainerProps) => ({
  ...(theme.custom.typography.h2 as any),
  color: theme.custom.colors.white,
  margin: 0,
  fontSize: isMobile ? '2.4rem' : theme.custom.fontSize.xLarge
}));

const OpeningTimes: any = styled.div(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.xLarge
}));

const OpeningTimesTitle: any = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any),
  color: theme.custom.colors.group1.base
}));

const OpeningTimesTimes: any = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  color: theme.custom.colors.white
}));

/* ********************************* *
 *    CallToBook                     *
 * Usage:                            *
 * <CallToBook tripType={tripType} destination={cmsDestination} /> *
 * ********************************* */

export const CallToBookComponent = ({ theme, testId, tripType, destination }: CallToBookProps) => {
  const t: any = useI18NextContext();

  if (isTourTripType(tripType)) return null;

  const tripTypeKey = TripTypeKeys[tripType] || TripTypeKeys[TripTypes.HOLIDAYS];
  const tripTypeLabel = t(tripTypeKey);

  return (
    <Container data-testid={testId || TestId.callToBook.main} isMobile={isMobile()} theme={theme}>
      <Header>
        {destination
          ? t('call-to-book--header', { destination: destination.name, tripType: tripTypeLabel })
          : t('call-to-book--no-destination-header', { tripType: tripTypeLabel })}
      </Header>
      <SubHeader isMobile={isMobile()}>{t('call-to-book--subheader')}</SubHeader>
      <OpeningTimes>
        <OpeningTimesTitle>{t('contact-details__title--opening-times')}</OpeningTimesTitle>
        <OpeningTimesTimes>{displayTimes}</OpeningTimesTimes>
      </OpeningTimes>
    </Container>
  );
};

export const CallToBook = withTheme(CallToBookComponent);
